/**
 * Gamification (Bodega P-1) — sync progress summary from the activity log.
 *
 * Derives a PURE `PlayerState` from the feed's sync audit entries
 * (`entity_type = "sync"`): XP, level/rank, backup streak and badges. There is no
 * write path or new tables — it is a recomputable local-first projection.
 * Private leagues by default; nothing punitive (a failed sync does not deduct).
 /** Waits for the app to finish booting (skeleton overlay disappears). */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Flame, Trophy } from "@fleet/icons";
import {
  type ActivityRow,
  BACKUP_STREAK_LINE,
  bodegaGamificationConfig,
  deriveGameState,
  levelProgress,
  rankForLevel,
} from "@bodega/shared";
import { Badge, Card, CardContent } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

/** Badge id → human-readable title (from the domain config). */
const BADGE_TITLES: Record<string, string> = Object.fromEntries(
  (bodegaGamificationConfig.badges ?? []).map((b) => [b.id, b.title]),
);

export function SyncProgress(props: { rows: () => readonly ActivityRow[] }) {
  const { t } = useI18n();

  const state = createMemo(() => deriveGameState(props.rows()));
  const rank = createMemo(() => rankForLevel(state().level));
  const progress = createMemo(() => levelProgress(state().xp));
  const streak = createMemo(() => state().streaks[BACKUP_STREAK_LINE]?.current ?? 0);
  const percent = createMemo(() => Math.round(progress().ratio * 100));

  // Human-readable badge labels (fallback: the id itself).
  const badgeLabel = (id: string) => BADGE_TITLES[id] ?? id;

  return (
    <Card data-testid="sync-progress">
      <CardContent class="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-3">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Trophy class="h-5 w-5" />
          </div>
          <div>
            <p class="text-sm font-semibold" data-testid="sync-progress-rank">
              {rank()} · {t(msg`Level`)} {state().level}
            </p>
            <p class="text-xs text-muted-foreground" data-testid="sync-progress-xp">
              {state().xp} {t(msg`XP`)}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-4">
          <div class="flex items-center gap-1.5 text-sm" data-testid="sync-progress-streak">
            <Flame
              class="h-4 w-4"
              classList={{
                "text-orange-500": streak() > 0,
                "text-muted-foreground opacity-40": streak() === 0,
              }}
            />
            <span class="tabular-nums font-medium">{streak()}</span>
            <span class="text-xs text-muted-foreground">{t(msg`day streak`)}</span>
          </div>

          <div class="hidden min-w-[8rem] sm:block">
            <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full rounded-full bg-primary transition-[width]"
                style={{ width: `${percent()}%` }}
              />
            </div>
          </div>
        </div>

        <Show when={state().badges.length > 0}>
          <div class="flex flex-wrap gap-1.5" data-testid="sync-progress-badges">
            <For each={state().badges}>
              {(id) => (
                <Badge variant="secondary" class="text-xs">
                  {badgeLabel(id)}
                </Badge>
              )}
            </For>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
